// Perception (decision 0049; the suggestions "senses: sight, hearing and think rate" and "perception delay"): sight
// (a cone and a range, then a line-of-sight check the game passes in, so this package never imports a physics
// library), hearing (noises with a range), and being hit. What an agent senses raises its awareness; awareness
// crosses thresholds into suspicious and alert; each rise takes effect only after a reaction delay, so a sudden
// sighting reads as a beat of "?" or "!" before the agent acts. Without stimulus, awareness decays and the state
// steps back down after a forget time. Plain data in, plain data out: deterministic.

/** [x, y, z] in metres. */
export type Vec3 = [number, number, number];

/** How alert an agent is. */
export type Alertness = "idle" | "suspicious" | "alert";

const RANK: Record<Alertness, number> = { idle: 0, suspicious: 1, alert: 2 };

/** The game's numbers for one kind of agent. */
export interface SenseTuning {
  sight: {
    /** Metres. */
    range: number;
    /** The cone's full angle (radians). */
    fov: number;
    /** Eye height above the agent's feet (m), for the line of sight. */
    eye: number;
    /** Awareness gained per second at point-blank range; it falls off linearly to `falloff` × that at full range. */
    gain: number;
    falloff: number;
    /** Within this range the cone doesn't matter (felt behind you). */
    near: number;
  };
  hearing: {
    /** Multiplies every noise's range (a deaf agent 0.5, a keen one 1.5). */
    scale: number;
    /** Awareness a heard noise sets at least (0 to 1). */
    awareness: number;
  };
  /** Awareness at which the agent turns suspicious, and alert (0 to 1). */
  suspiciousAt: number;
  alertAt: number;
  /** Seconds between crossing a threshold upward and the state changing (the reaction delay). */
  reaction: number;
  /** Awareness lost per second while nothing is sensed. */
  decay: number;
  /** Seconds without sight of the target before alert steps down to suspicious. */
  forgetAlert: number;
}

/** One agent's perception: plain data, saved with the game. */
export interface PerceptionState {
  level: Alertness;
  /** 0 to 1. */
  awareness: number;
  /** A rise waiting out the reaction delay: the level it goes to and the seconds left. */
  pending: { to: Alertness; left: number } | null;
  /** Who it is aware of (the best-seen candidate), and where it last knew them to be. */
  target: number | null;
  lastKnown: Vec3 | null;
  /** Sees the target now (after the line-of-sight check). */
  sees: boolean;
  /** Seconds since it last saw the target. */
  unseen: number;
  /** Why the last rise happened ("sight", "noise", "hit"), for debug views and specs. */
  cause: "sight" | "noise" | "hit" | null;
}

/** Something the agent might see. */
export interface Candidate {
  id: number;
  pos: Vec3;
  /** Its height to aim the line-of-sight ray at (m above `pos`). */
  height?: number;
}

/** A noise: where, how far it carries (m), who made it. */
export interface Noise {
  pos: Vec3;
  range: number;
  source: number | null;
}

/** What happened to the agent this think. */
export interface Stimuli {
  candidates: readonly Candidate[];
  noises: readonly Noise[];
  /** Hit by this entity since the last think (being hit is a sense: straight to alert, after the reaction delay). */
  hitBy: number | null;
  /** Where the hit came from (the attacker's position), when known. */
  hitFrom?: Vec3;
}

/** The game's line-of-sight check: true when nothing blocks the segment. */
export type LineOfSight = (from: Vec3, to: Vec3) => boolean;

/** A fresh, idle perception. */
export function createPerception(): PerceptionState {
  return { level: "idle", awareness: 0, pending: null, target: null, lastKnown: null, sees: false, unseen: 1e9, cause: null };
}

/** Planted defects, beside what they break. */
export const PERCEPTION_FAULTS = {
  /** No reaction delay: rises take effect on the think they're sensed (the perception tests must go red). */
  noReaction: false,
  /** The cone is ignored: anything in range is seen (the sight-cone test must go red). */
  noCone: false,
};

/** Whether `self` sees `c` (range, cone, then the line of sight); returns the distance or -1. */
export function canSee(self: { pos: Vec3; facing: number }, c: Candidate, t: SenseTuning, los: LineOfSight): number {
  const dx = c.pos[0] - self.pos[0];
  const dz = c.pos[2] - self.pos[2];
  const d = Math.hypot(dx, dz);
  if (d > t.sight.range) return -1;
  if (d > t.sight.near && !PERCEPTION_FAULTS.noCone) {
    // facing is a yaw: 0 looks down +z, positive turns toward +x
    const fx = Math.sin(self.facing);
    const fz = Math.cos(self.facing);
    const cos = (dx * fx + dz * fz) / (d || 1);
    if (cos < Math.cos(t.sight.fov / 2)) return -1;
  }
  const eye: Vec3 = [self.pos[0], self.pos[1] + t.sight.eye, self.pos[2]];
  const at: Vec3 = [c.pos[0], c.pos[1] + (c.height ?? 1), c.pos[2]];
  return los(eye, at) ? d : -1;
}

/** One think: sense, raise or decay awareness, step the state with its reaction delay. */
export function perceive(st: PerceptionState, self: { pos: Vec3; facing: number }, s: Stimuli, t: SenseTuning, los: LineOfSight, dt: number): void {
  // sight: the closest seen candidate is the target
  let best: Candidate | null = null;
  let bestD = Infinity;
  for (const c of s.candidates) {
    const d = canSee(self, c, t, los);
    if (d >= 0 && d < bestD) {
      best = c;
      bestD = d;
    }
  }
  st.sees = best !== null;
  let want: Alertness = st.level;
  let cause: PerceptionState["cause"] = null;
  if (best) {
    st.target = best.id;
    st.lastKnown = [...best.pos];
    st.unseen = 0;
    const k = 1 - (1 - t.sight.falloff) * Math.min(1, bestD / t.sight.range);
    st.awareness = Math.min(1, st.awareness + t.sight.gain * k * dt);
    cause = "sight";
  } else {
    st.unseen += dt;
  }
  // hearing: a noise within its (scaled) range sets awareness at least to the hearing level, and where to look
  for (const n of s.noises) {
    const d = Math.hypot(n.pos[0] - self.pos[0], n.pos[2] - self.pos[2]);
    if (d > n.range * t.hearing.scale) continue;
    if (st.awareness < t.hearing.awareness) {
      st.awareness = t.hearing.awareness;
      cause ??= "noise";
    }
    if (!st.sees) st.lastKnown = [...n.pos];
    if (n.source !== null && st.target === null) st.target = n.source;
  }
  // being hit: full awareness of the attacker
  if (s.hitBy !== null) {
    st.awareness = 1;
    st.target = s.hitBy;
    st.unseen = 0;
    if (s.hitFrom) st.lastKnown = [...s.hitFrom];
    cause = "hit";
  }
  if (!best && s.noises.length === 0 && s.hitBy === null) st.awareness = Math.max(0, st.awareness - t.decay * dt);

  // alert holds until the target has gone unseen for the forget time (and nothing new hit it), then steps down
  if (st.level === "alert" && st.unseen >= t.forgetAlert && s.hitBy === null) st.awareness = Math.min(st.awareness, t.alertAt - 1e-6);
  // the level awareness asks for
  if (st.awareness >= t.alertAt) want = "alert";
  else if (st.awareness >= t.suspiciousAt) want = "suspicious";
  else want = "idle";
  // hysteresis: suspicious holds until awareness falls to half its threshold; alert until the forget time
  if (st.level === "suspicious" && want === "idle" && st.awareness > t.suspiciousAt * 0.5) want = "suspicious";
  if (st.level === "alert" && want !== "alert") {
    if (st.unseen < t.forgetAlert) want = "alert";
    else {
      // alert steps down to suspicious (searching where it last knew the target), never straight to idle
      want = "suspicious";
      st.awareness = t.alertAt - 1e-6;
    }
  }

  if (RANK[want] > RANK[st.level]) {
    // a rise waits out the reaction delay (a higher one restarts nothing: it keeps the time already waited)
    if (PERCEPTION_FAULTS.noReaction || t.reaction <= 0) {
      st.level = want;
      st.pending = null;
      st.cause = cause ?? st.cause;
    } else if (!st.pending) st.pending = { to: want, left: t.reaction };
    else st.pending.to = RANK[want] > RANK[st.pending.to] ? want : st.pending.to;
    if (st.pending) {
      st.pending.left -= dt;
      if (st.pending.left <= 1e-9) {
        st.level = st.pending.to;
        st.pending = null;
        st.cause = cause ?? st.cause;
      }
    }
  } else {
    // falling (or holding) cancels a pending rise; falls are immediate
    st.pending = null;
    if (RANK[want] < RANK[st.level]) st.level = want;
  }
  if (st.level === "idle") {
    st.target = st.sees ? st.target : null;
    if (st.awareness === 0) st.lastKnown = null;
  }
}
